import React, { useState, useEffect } from 'react';
import SideNavbar from '../../../components/SideNavbar';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSubAgentAuth } from '../SubAgent/SubAgentAuthContext';
import { useAuth } from '../../../AuthContext';
import { db, auth } from '../../../firebaseConfig';
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';

// Redux
import { setStudentDetailsView } from '../../../redux/studentDetailsViewSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/store';

// Screens 
import Grid_1 from './StudentDetailsScreen_1';
import Grid_2 from './StudentDetailsScreen_2';
import Timeline from './HorizontalTimeline';

import LeadEdit from '../../../components/modals/LeadEditModal';

// Action modals
import CollaborateModal from '../../../components/Actions/modal/CollaborateModal';
import TransferModal from '../../../components/Actions/modal/TransferModal';
import FollowUpModal from '../../../components/modals/FollowUpModal';
import Toast from '../../../components/Toast';

// Cloud telephony
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../firebaseConfig';

// Add these imports at the top
import { LeadData } from '../../../pages/Clients/Admin/Function/LeadFunctions';
import { collection, query, where, getDocs } from 'firebase/firestore';

// Add these interfaces above the component
interface FollowUp {
    id: string;
    leadId: string;
    followUp: string;
    followUpTime: Timestamp;
    userId: string;
    isDone: boolean;
    createdAt?: Timestamp;
}


interface SubmittedDocument {
    name: string;
    file: File;
    uploadTime: Date;
    iconPath: string;
}

const StudentDetails = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { leadId } = useParams();
    const { currentSubAgent } = useSubAgentAuth();
    const { departmentData, isAdmin, isStaff } = useAuth();

    // Get navigation state from leadManagement
    const navigationState = location.state;
    const previousPage = navigationState?.from;

    const handleBackNavigation = () => {
        if (previousPage && navigationState) {
            // Navigate back with state restoration flags if we have navigation state
            navigate(previousPage, {
                replace: false,
                state: {
                    ...navigationState,
                    shouldRestoreScroll: true,
                    returnedFromStudentDetails: true
                }
            });
        } else {
            // Fallback to browser history navigation
            navigate(-1);
        }
    };

    const [studentName, setStudentName] = useState<string>("Loading...");
    const [studentPhoneNumber, setStudentPhoneNumber] = useState<string>("");
    const [studentEmail, setStudentEmail] = useState<string>("");
    const [studentAlternateNumber1, setStudentAlternateNumber1] = useState<string>("");
    const [studentAlternateNumber2, setStudentAlternateNumber2] = useState<string>("");
    const [isTimelineCollapsed, setIsTimelineCollapsed] = useState(true);
    const [view, setView] = useState('stack');
    const [isLeadActive, setIsLeadActive] = useState<boolean>(true);
    const [isDeactivating, setIsDeactivating] = useState<boolean>(false);

    const [pipelineCount, setPipelineCount] = useState<number>(0);

    // Follow-up related states
    const [activeFollowUp, setActiveFollowUp] = useState<FollowUp | null>(null);
    const [isLoadingFollowUp, setIsLoadingFollowUp] = useState<boolean>(false);
    const [isMarkingDone, setIsMarkingDone] = useState<boolean>(false);

    // Inside the StudentDetails component, add these states:
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [currentLead, setCurrentLead] = useState<LeadData | null>(null);

    // Action buttons and modals state
    const [selectedStudent, setSelectedStudent] = useState<LeadData | null>(null);
    const [selectedStudentPhone, setSelectedStudentPhone] = useState<string>('');
    const [currentAssignedUser, setCurrentAssignedUser] = useState<{ id: string, name: string } | null>(null);
    const [companyCloudTelephony, setCompanyCloudTelephony] = useState<boolean>(false);
    const [callingLeads, setCallingLeads] = useState<{ [key: string]: boolean }>({});

    // Modal states
    const [showCollaborateModal, setShowCollaborateModal] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [showFollowUpModal, setShowFollowUpModal] = useState(false);

    // Toast states
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [isError, setIsError] = useState(false);

    // Confirmation modal states
    const [showDeactivateModal, setShowDeactivateModal] = useState(false);


    // Redux dispatch
    const dispatch = useAppDispatch();
    const isCollapsed = useAppSelector(state => state.sidebar.isCollapsed);
    const currentView = useAppSelector(state => state.studentDetailsView.studentDetailsView);

    // Move fetchStudentData outside so it can be reused
    const fetchStudentData = async () => {
        try {
            const currentUser = auth.currentUser;
            if (!currentUser || !leadId) return;

            let companyId: string | undefined;
            // Get user document to get companyId
            if (currentSubAgent) {
                companyId = currentSubAgent.companyId;
            } else {
                const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                companyId = userDoc.data()?.companyId;
            }

            if (!companyId) return;

            // Get lead document
            const leadDoc = await getDoc(doc(db, `companies/${companyId}/leads/${leadId}`));
            if (leadDoc.exists()) {
                const leadData = leadDoc.data();

                // Set current lead data for editing
                setCurrentLead({
                    id: leadId,
                    ...leadData
                } as LeadData);

                setStudentName(leadData.name || "Unknown Student");
                setStudentPhoneNumber(leadData.number || "No Phone Number");
                setStudentEmail(leadData.email || "No Email");
                setStudentAlternateNumber1(leadData.alternateNumber1 || "");
                setStudentAlternateNumber2(leadData.alternateNumber2 || "");
                setIsLeadActive(leadData.isActive !== false); // Default to true if undefined
                console.log("Student data fetched successfully:", leadData);
            }
        } catch (error) {
            console.error("Error fetching student data:", error);
            setStudentName("Error loading student");
            setStudentPhoneNumber("N/A");
            setStudentEmail("N/A");
        }
    };

    // Fetch active follow-up for the lead
    const fetchActiveFollowUp = async () => {
        setIsLoadingFollowUp(true);
        try {
            const currentUser = auth.currentUser;
            if (!currentUser || !leadId) return;

            let companyId: string | undefined;
            // Get user document to get companyId
            if (currentSubAgent) {
                companyId = currentSubAgent.companyId;
            } else {
                const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                companyId = userDoc.data()?.companyId;
            }

            if (!companyId) return;

            // Query for active follow-ups for this lead
            const followUpsRef = collection(db, `companies/${companyId}/followUps`);
            const followUpQuery = query(
                followUpsRef,
                where('leadId', '==', leadId),
                where('isDone', '==', false)
            );

            const querySnapshot = await getDocs(followUpQuery);
            if (!querySnapshot.empty) {
                const followUpDoc = querySnapshot.docs[0];
                const followUpData = {
                    id: followUpDoc.id,
                    ...followUpDoc.data()
                } as FollowUp;
                setActiveFollowUp(followUpData);
            } else {
                setActiveFollowUp(null);
            }
        } catch (error) {
            console.error("Error fetching active follow-up:", error);
        } finally {
            setIsLoadingFollowUp(false);
        }
    };

    // Mark follow-up as done
    const handleMarkFollowUpDone = async () => {
        if (!activeFollowUp || isMarkingDone) return;

        setIsMarkingDone(true);
        try {
            const currentUser = auth.currentUser;
            if (!currentUser) return;

            let companyId: string | undefined;
            if (currentSubAgent) {
                companyId = currentSubAgent.companyId;
            } else {
                const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                companyId = userDoc.data()?.companyId;
            }

            if (!companyId) return;

            // Update follow-up as done
            const followUpRef = doc(db, `companies/${companyId}/followUps/${activeFollowUp.id}`);
            await updateDoc(followUpRef, {
                isDone: true,
                isEdited: false,
                completedAt: Timestamp.fromDate(new Date()),
                completedBy: currentUser.uid
            });

            // Update lead to remove active follow-up
            const leadRef = doc(db, `companies/${companyId}/leads/${leadId}`);
            await updateDoc(leadRef, {
                hasActiveFollowUp: false,
                activeFollowUpId: null,
                updatedAt: Timestamp.fromDate(new Date()),
                updatedBy: currentUser.uid,
                activeFollowUpTime: null,
                activeFollowUpText: null,
            });

            // Clear active follow-up state
            setActiveFollowUp(null);

            console.log("Follow-up marked as done successfully");

        } catch (error) {
            console.error("Error marking follow-up as done:", error);
        } finally {
            setIsMarkingDone(false);
        }
    };

    // Action handlers for the action buttons
    const handleCollaborateClick = async () => {
        if (!leadId) return;

        const leadData: LeadData = {
            id: leadId,
            name: studentName,
            number: studentPhoneNumber,
            email: studentEmail,
        } as LeadData;

        setSelectedStudent(leadData);
        setSelectedStudentPhone(studentPhoneNumber);
        setShowCollaborateModal(true);
    };

    const handleTransferClick = async () => {
        if (!leadId) return;

        try {
            let companyId: string | undefined;
            if (currentSubAgent) {
                companyId = currentSubAgent.companyId;
            } else {
                const currentUser = auth.currentUser;
                if (currentUser) {
                    const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                    companyId = userDoc.data()?.companyId;
                }
            }

            if (!companyId) return;

            const leadRef = doc(db, `companies/${companyId}/leads/${leadId}`);
            const leadDoc = await getDoc(leadRef);

            if (leadDoc.exists()) {
                const leadData = leadDoc.data();
                const leadDataObject: LeadData = {
                    id: leadId,
                    name: studentName,
                    number: studentPhoneNumber,
                    email: studentEmail,
                    assignedTo: leadData.assignedTo,
                } as LeadData;

                setSelectedStudent(leadDataObject);
                setSelectedStudentPhone(studentPhoneNumber);

                // Fetch current assigned user's name for TransferModal
                if (leadData.assignedTo) {
                    const userDoc = await getDoc(doc(db, 'users', leadData.assignedTo));
                    if (userDoc.exists()) {
                        setCurrentAssignedUser({
                            id: userDoc.id,
                            name: userDoc.data().name
                        });
                    }
                }
            }
        } catch (error) {
            console.error('Error in handleTransferClick:', error);
        }

        setShowTransferModal(true);
    };

    const handleFollowUpClick = async () => {
        if (!leadId) return;

        const leadDataObject: LeadData = {
            id: leadId,
            name: studentName,
            number: studentPhoneNumber,
            email: studentEmail,
        } as LeadData;

        setSelectedStudent(leadDataObject);
        setShowFollowUpModal(true);
    };

    const handleCall = async () => {
        if (!leadId) return;

        try {
            // Set calling state for this specific lead
            setCallingLeads(prev => ({ ...prev, [leadId]: true }));

            // Make sure we have a phone number to call
            if (!studentPhoneNumber) {
                setToastMessage('Cannot initiate call: Missing phone number');
                setShowToast(true);
                setIsError(true);
                setCallingLeads(prev => ({ ...prev, [leadId]: false }));
                return;
            }

            // Clean the phone number to ensure it's in the correct format
            const cleanNumber = studentPhoneNumber.trim();

            // Call the cloud telephony function
            console.log("Initiating call to:", cleanNumber);

            let companyId: string | undefined;
            if (currentSubAgent) {
                companyId = currentSubAgent.companyId;
            } else {
                const currentUser = auth.currentUser;
                if (currentUser) {
                    const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                    companyId = userDoc.data()?.companyId;
                }
            }

            if (!companyId) return;

            // Get company data
            const company = await getDoc(doc(db, `companies/${companyId}`));
            if (!company.exists()) {
                console.error('Company data not found');
                setToastMessage('Company data not found. Please try again later.');
                setShowToast(true);
                setIsError(true);
                setCallingLeads(prev => ({ ...prev, [leadId]: false }));
                return;
            }

            const cloudTelephonyProvider = company.data()?.cloudTelephonyProvider;
            console.log('Cloud Telephony Provider:', cloudTelephonyProvider);

            // Determine which cloud function to use
            let initiateCallFn;
            if (cloudTelephonyProvider === 'exotel') {
                initiateCallFn = httpsCallable(functions, 'initiateExotelCall');
            } else if (cloudTelephonyProvider === 'airtel') {
                initiateCallFn = httpsCallable(functions, 'initiateAirtelCall');
            } else {
                console.error('Unsupported cloud telephony provider:', cloudTelephonyProvider);
                setToastMessage('Unsupported cloud telephony provider. Please contact support.');
                setShowToast(true);
                setIsError(true);
                setCallingLeads(prev => ({ ...prev, [leadId]: false }));
                return;
            }

            const result = await initiateCallFn({
                to: cleanNumber,
                leadId: leadId
            });

            console.log('Call initiation result:', result.data);

            // Show success message
            setToastMessage('Call initiated successfully!');
            setShowToast(true);
            setIsError(false);

        } catch (error) {
            console.error('Error initiating call:', error);
            setToastMessage('Failed to initiate call. Please try again.');
            setShowToast(true);
            setIsError(true);
        } finally {
            setCallingLeads(prev => ({ ...prev, [leadId]: false }));
            setTimeout(() => {
                setShowToast(false);
            }, 3000);
        }
    };

    // Open whats app web in a new tab with the student's phone number
    const openWhatsAppWeb = (phoneNumber: string) => {
        const formattedNumber = phoneNumber.replace(/\D/g, ''); // Remove non-numeric characters
        const url = `https://web.whatsapp.com/send?phone=${formattedNumber}`;
        window.open(url, '_blank');
    };

    useEffect(() => {
        fetchStudentData();
        fetchActiveFollowUp();
    }, [leadId, currentSubAgent]);

    useEffect(() => {
        setView(currentView);
    }, [currentView]);

    const toggleTimelineCollapse = () => {
        setIsTimelineCollapsed(!isTimelineCollapsed);
    };

    const handleToogleView = (view: string) => {
        setView(view);
        dispatch(setStudentDetailsView({ key: 'studentDetailsView', value: view }));
    };

    const handleEditLead = async () => {
        console.log("Edit button clicked"); // Debug log
        try {
            // Simply open the modal - LeadEditModal will handle its own data fetching
            setIsEditModalOpen(true);
        } catch (error) {
            console.error("Error in handleEditLead:", error);
        }
    };

    const handleToggleLeadStatus = async () => {
        if (!leadId || isDeactivating) return;

        // Show confirmation dialog when deactivating (changing from active to inactive)
        if (isLeadActive) {
            setShowDeactivateModal(true);
            return; // Don't proceed until user confirms
        }

        // If activating (changing from inactive to active), proceed directly
        await toggleLeadStatusConfirmed();

    };

    const toggleLeadStatusConfirmed = async () => {
        setIsDeactivating(true);
        try {
            const currentUser = auth.currentUser;
            if (!currentUser) return;

            let companyId: string | undefined;
            // Get user document to get companyId
            if (currentSubAgent) {
                companyId = currentSubAgent.companyId;
            } else {
                const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                companyId = userDoc.data()?.companyId;
            }

            if (!companyId) return;

            // Update lead status
            const leadRef = doc(db, `companies/${companyId}/leads/${leadId}`);
            await updateDoc(leadRef, {
                isActive: !isLeadActive,
                updatedAt: Timestamp.fromDate(new Date()),
                updatedBy: currentUser.uid
            });

            // Update local state
            setIsLeadActive(!isLeadActive);

            // Show success message
            const statusMessage = !isLeadActive ? 'Lead activated successfully' : 'Lead deactivated successfully';
            setToastMessage(statusMessage);
            setShowToast(true);
            setIsError(false);

            // If deactivating, navigate to lead management after a short delay
            if (isLeadActive) {
                setTimeout(() => {
                    if (previousPage && navigationState) {
                        navigate(previousPage, {
                            replace: false,
                            state: {
                                ...navigationState,
                                shouldRestoreScroll: true,
                                returnedFromStudentDetails: true
                            }
                        });
                    } else {
                        // Fallback to direct navigation
                        navigate('/leadManagement');
                    }
                }, 1500);
            }
        } catch (error) {
            console.error("Error toggling lead status:", error);
            setToastMessage("Error updating lead status");
            setShowToast(true);
            setIsError(true);
        } finally {
            setIsDeactivating(false);
            setShowDeactivateModal(false);
            // to also activate the handleMarkFollowUpDone if activating the lead
            await handleMarkFollowUpDone();
        }
    };

    useEffect(() => {
        const loadData = async () => {
            await fetchStudentData();
            await fetchActiveFollowUp();
        };

        if (leadId) {
            loadData();
        }
    }, [leadId, currentSubAgent]);

    // Load company cloud telephony settings
    useEffect(() => {
        const loadCompanySettings = async () => {
            try {
                let companyId: string | undefined;
                if (currentSubAgent) {
                    companyId = currentSubAgent.companyId;
                } else {
                    const currentUser = auth.currentUser;
                    if (currentUser) {
                        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                        companyId = userDoc.data()?.companyId;
                    }
                }

                if (!companyId) return;

                const company = await getDoc(doc(db, `companies/${companyId}`));
                if (company.exists()) {
                    setCompanyCloudTelephony(company.data()?.cloudTelephony || false);
                }
            } catch (error) {
                console.error('Error loading company settings:', error);
            }
        };

        loadCompanySettings();
    }, [currentSubAgent]);

    // Function to check if action is permitted
    const getActionPermission = (action: string) => {
        const actions = departmentData?.actionPermission || [];
        const actionPermission = Object.values(actions).find(item => item.name === action);
        return actionPermission ? actionPermission.allowed : false;
    };


    return (
        <div className="min-h-screen bg-gray-200">
            <div className="fixed top-16 left-0 h-full z-10">
                <SideNavbar />
            </div>
            <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} mt-[109px] p-8 transition-all duration-300`}>
                <div className="bg-white p-6 rounded-3xl shadow-md w-full flex flex-col">
                    <div className="flex items-center gap-4 mb-2">
                        <>
                            <button className="text-gray-600 hover:text-gray-800">
                                <img
                                    src={`${process.env.PUBLIC_URL}/images/desktop/backward.svg`}
                                    alt="Back Arrow"
                                    onClick={handleBackNavigation}
                                    className="w-6 h-6"
                                />
                            </button>
                            <p className="font-montserrat font-medium text-gray-900 text-xl">Back</p>
                        </>
                        <div className="flex-grow"></div>
                        {/* toggle to change view */}
                        <div className="flex items-center gap-4">
                            <button
                                title='Stack'
                                className={`text-gray-600 hover:text-gray-800 p-2 rounded-xl ${view === 'stack' ? 'bg-slate-200' : ''}`}
                                onClick={() => handleToogleView('stack')}
                            >
                                <img
                                    src={`${process.env.PUBLIC_URL}/images/desktop/rectangle-stack.svg`}
                                    alt="Toggle View"
                                    className="w-6 h-6"
                                />
                            </button>
                            <button
                                title='Grid'
                                className={`text-gray-600 hover:text-gray-800 p-2 rounded-xl ${view === 'grid' ? 'bg-slate-200' : ''}`}
                                onClick={() => handleToogleView('grid')}
                            >
                                <img
                                    src={`${process.env.PUBLIC_URL}/images/desktop/rectangle-grid.svg`}
                                    alt="Toggle View"
                                    className="w-6 h-6"
                                />
                            </button>
                        </div>
                    </div>
                    <div className="flex items-center justify-between bg-white p-3 shadow-md rounded-lg mb-4 border border-gray-200 border-s-[#08304a] border-l-4">
                        {/* student info banner */}
                        <div className="items-center gap-4">
                            <div className="text-gray-700 font-medium text-2xl flex items-center gap-2">
                                <span className="font-semibold"></span> {studentName}
                                {/* {!isLeadActive && (
                                    <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
                                        Inactive
                                    </span>
                                )} */}
                            </div>
                            <div className="text-gray-700 font-medium text-lg">
                                <span className="font-semibold">Phone:</span> {studentPhoneNumber}
                            </div>
                            <div className="text-gray-700 font-medium text-lg">
                                <span className="font-semibold">Email:</span> {studentEmail}
                            </div>
                            {/* Alternate phone numbers - only show if at least one exists */}
                            {(studentAlternateNumber1 || studentAlternateNumber2) && (
                                <div className="text-gray-700 font-medium text-lg">
                                    <span className="font-semibold">Alternate Numbers:</span>
                                    <div className="ml-4 mt-1">
                                        {studentAlternateNumber1 && (
                                            <div className="text-gray-600">• {studentAlternateNumber1}</div>
                                        )}
                                        {studentAlternateNumber2 && (
                                            <div className="text-gray-600">• {studentAlternateNumber2}</div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>


                        <div className="flex items-center gap-4">
                            {/* Toggle Switch for Lead Status - Only show if permission is granted */}
                            {getActionPermission('Delete') && (
                                <>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-gray-700">
                                            {isLeadActive ? "Active" : "Inactive"}
                                        </span>
                                        <button
                                            onClick={handleToggleLeadStatus}
                                            disabled={isDeactivating}
                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${isLeadActive ? 'bg-green-500' : 'bg-red-500'
                                                } ${isDeactivating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                            title={isLeadActive ? "Click to deactivate lead" : "Click to activate lead"}
                                        >
                                            {isDeactivating ? (
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                </div>
                                            ) : (
                                                <span
                                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out ${isLeadActive ? 'translate-x-6' : 'translate-x-1'
                                                        }`}
                                                />
                                            )}
                                        </button>
                                    </div>

                                    {/* Separator */}
                                    <div className="h-8 w-px bg-gray-300"></div>
                                </>
                            )}

                            {/* Follow-up Quick Actions */}
                            <div className="flex items-center gap-2">
                                {isLoadingFollowUp ? (
                                    <div className="flex items-center gap-2 px-3 py-2 text-sm text-gray-500">
                                        <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
                                        Loading...
                                    </div>
                                ) : activeFollowUp ? (
                                    <div className="flex items-center gap-2">
                                        {/* Active Follow-up Status */}
                                        <div className="flex items-center gap-2 px-3 py-2 bg-orange-100 text-orange-800 rounded-lg text-sm relative group cursor-pointer">
                                            <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                                            <span className="font-medium">Active Follow-up</span>
                                            <span className="text-xs">
                                                {activeFollowUp.followUpTime &&
                                                    new Date(activeFollowUp.followUpTime.toMillis()).toLocaleDateString()
                                                }
                                            </span>

                                            {/* Tooltip */}
                                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap max-w-xs z-10 pointer-events-none">
                                                <div className="break-words whitespace-normal">
                                                    {activeFollowUp.followUp || "No description available"}
                                                </div>
                                                {/* Tooltip arrow */}
                                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                                            </div>
                                        </div>

                                        {/* Mark as Done Button */}
                                        <button
                                            onClick={handleMarkFollowUpDone}
                                            disabled={isMarkingDone}
                                            className="flex items-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                            title="Mark follow-up as done"
                                        >
                                            {isMarkingDone ? (
                                                <>
                                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                    Marking...
                                                </>
                                            ) : (
                                                <>
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                    Mark Done
                                                </>
                                            )}
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        No Active Follow-up
                                    </div>
                                )}
                            </div>

                            {/* Separator */}
                            <div className="h-8 w-px bg-gray-300"></div>

                            {/* Action Buttons */}
                            <div className="flex items-center gap-1">
                                {!getActionPermission('Delete') && !getActionPermission('Edit') && !getActionPermission('Details') && !getActionPermission('Collaborate') && !getActionPermission('Transfer') && !getActionPermission('Follow Up') && !getActionPermission('Cloud Telephony') && (
                                    <button className="p-1 hover:bg-gray-100 rounded relative group">
                                        <span className="absolute bottom-3 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-80 text-white text-xs rounded px-2 py-1 mb-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                                            You are not authorized to perform any <br /> actions. Contact admin for details.
                                        </span>
                                        <img
                                            src={`${process.env.PUBLIC_URL}/images/desktop/info-circle.svg`}
                                            alt="More Icon"
                                            className="w-5 h-5 cursor-pointer"
                                        />
                                    </button>
                                )}
                                {getActionPermission('Edit') && (
                                    <button className="p-1 hover:bg-gray-100 rounded relative group">
                                        <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 bg-black bg-opacity-80 text-white text-xs rounded px-2 py-1 mb-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                                            Edit Lead
                                        </span>
                                        <img
                                            src={`${process.env.PUBLIC_URL}/images/desktop/editicon.svg`}
                                            alt="Edit Icon"
                                            onClick={handleEditLead}
                                            className="w-5 h-5 cursor-pointer"
                                        />
                                    </button>
                                )}
                                {getActionPermission('Collaborate') && (
                                    <button className="p-1 hover:bg-gray-100 rounded relative group">
                                        <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 bg-black bg-opacity-80 text-white text-xs rounded px-2 py-1 mb-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                                            Collaborate
                                        </span>
                                        <img
                                            src={`${process.env.PUBLIC_URL}/images/desktop/collaborate.svg`}
                                            alt="collaborate-icon"
                                            className="w-5 h-5 cursor-pointer"
                                            onClick={handleCollaborateClick}
                                        />
                                    </button>
                                )}
                                {getActionPermission('Transfer') && (
                                    <button className="p-1 hover:bg-gray-100 rounded relative group">
                                        <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 bg-black bg-opacity-80 text-white text-xs rounded px-2 py-1 mb-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                                            Transfer Lead
                                        </span>
                                        <img
                                            src={`${process.env.PUBLIC_URL}/images/desktop/TransferLead.svg`}
                                            alt="transfer-lead-icon"
                                            className="w-5 h-5 cursor-pointer"
                                            onClick={handleTransferClick}
                                        />
                                    </button>
                                )}
                                {getActionPermission('Follow Up') && (
                                    <button className="p-1 hover:bg-gray-100 rounded relative group">
                                        <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 bg-black bg-opacity-80 text-white text-xs rounded px-2 py-1 mb-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                                            Follow Up
                                        </span>
                                        <img
                                            src={`${process.env.PUBLIC_URL}/images/desktop/follow-up-leadMngt.svg`}
                                            alt="follow-up-icon"
                                            className="w-5 h-5 cursor-pointer"
                                            onClick={handleFollowUpClick}
                                        />
                                    </button>
                                )}
                                {getActionPermission('Cloud Telephony') && companyCloudTelephony && (
                                    <button className="p-1 hover:bg-gray-100 rounded relative group">
                                        <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 bg-black bg-opacity-80 text-white text-xs rounded px-2 py-1 mb-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                                            Call
                                        </span>
                                        {callingLeads[leadId || ''] ? (
                                            <div className="w-5 h-5 flex items-center justify-center">
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                            </div>
                                        ) : (
                                            <img
                                                src={`${process.env.PUBLIC_URL}/images/desktop/cloudTelephony.svg`}
                                                alt="call-icon"
                                                className="w-5 h-5 cursor-pointer"
                                                onClick={handleCall}
                                            />
                                        )}
                                    </button>
                                )}
                            </div>

                            <div className="h-8 w-px bg-gray-300"></div>


                            <img
                                src={`${process.env.PUBLIC_URL}/images/desktop/whatsappLogo.svg`}
                                alt="whatsapp-logo"
                                title='Share on WhatsApp'
                                className="w-9 h-9 ml-2 cursor-pointer"
                                onClick={() => openWhatsAppWeb(studentPhoneNumber)}
                            />
                        </div>
                    </div>

                    {/* Collapsible Timeline Section */}
                    <div className="h-fit bg-white p-3 shadow-md rounded-lg mb-4 border border-gray-200 border-s-[#08304a] border-l-4">
                        <div
                            className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded w-full"
                            onClick={toggleTimelineCollapse}
                        >
                            <h3 className="font-medium text-gray-700">Application Timeline</h3>
                            <div className="flex items-center gap-2">
                                <div className="text-sm text-gray-600">
                                    {pipelineCount} pipeline{pipelineCount !== 1 ? 's' : ''} configured
                                </div>
                                <button
                                    className="text-gray-500 hover:text-gray-700 focus:outline-none"
                                    aria-label={isTimelineCollapsed ? "Expand timeline" : "Collapse timeline"}
                                >
                                    {isTimelineCollapsed ? (
                                        <span className="flex items-center">
                                            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </span>
                                    ) : (
                                        <span className="flex items-center">
                                            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                            </svg>
                                        </span>
                                    )}
                                </button>
                            </div>
                        </div>
                        <div
                            className={`transition-all duration-300 ease-in-out ${isTimelineCollapsed ? 'max-h-0 opacity-0 overflow-hidden' : 'max-h-[1000px] opacity-100'}`}
                        >
                            <div className="pt-3">
                                <Timeline leadId={leadId} onPipelineCountChange={setPipelineCount} />
                            </div>

                        </div>
                    </div>

                    {view === 'stack' && (
                        <>
                            <Grid_1 />
                        </>
                    )}

                    {view === 'grid' && (
                        <>
                            <Grid_2 />
                        </>
                    )}
                </div>
            </div>

            {/* Toast Notification */}
            {showToast && (
                <Toast
                    message={toastMessage}
                    isError={isError}
                    show={showToast}
                    onClose={() => setShowToast(false)}
                />
            )}

            {/* Collaborate Modal */}
            {showCollaborateModal && (
                <CollaborateModal
                    isOpen={showCollaborateModal}
                    selectedStudent={selectedStudent}
                    selectedStudentPhone={selectedStudentPhone}
                    onClose={() => setShowCollaborateModal(false)}
                    onSuccess={(message: string) => {
                        setToastMessage(message);
                        setShowToast(true);
                        setIsError(false);
                    }}
                />
            )}

            {/* Transfer Modal */}
            {showTransferModal && (
                <TransferModal
                    isOpen={showTransferModal}
                    selectedStudent={selectedStudent}
                    selectedStudentPhone={selectedStudentPhone}
                    currentAssignedUser={currentAssignedUser}
                    onClose={() => setShowTransferModal(false)}
                    onSuccess={(message: string) => {
                        setToastMessage(message);
                        setShowToast(true);
                        setIsError(false);
                    }}
                />
            )}

            {/* Follow Up Modal */}
            {showFollowUpModal && selectedStudent && (
                <FollowUpModal
                    isOpen={showFollowUpModal}
                    onClose={() => setShowFollowUpModal(false)}
                    selectedStudent={{
                        id: selectedStudent.id,
                        name: selectedStudent.name,
                        phone: selectedStudent.number || ''
                    }}
                />
            )}


            {/* Lead Edit Modal */}
            {isEditModalOpen && (
                <LeadEdit
                    isOpen={isEditModalOpen}
                    currentUser={auth.currentUser}
                    isStaff={isStaff}
                    currentScreen="StudentDetails"
                    leadId={leadId || ''}
                    onClose={() => {
                        setIsEditModalOpen(false);
                        // Refresh the student data when modal closes to reflect any changes
                        fetchStudentData();
                    }}
                />
            )}

            {/* Deactivate Confirmation Modal */}
            {showDeactivateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
                        <div className="flex items-center mb-4">
                            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                            </div>
                            <div className="ml-4">
                                <h3 className="text-lg font-medium text-gray-900">
                                    Deactivate Lead
                                </h3>
                            </div>
                        </div>

                        <div className="mb-6">
                            <p className="text-sm text-gray-600">
                                Are you sure you want to deactivate this lead for <strong>{studentName}</strong>?
                            </p>
                        </div>

                        <div className="flex items-center justify-end space-x-3">
                            <button
                                type="button"
                                onClick={() => setShowDeactivateModal(false)}
                                disabled={isDeactivating}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={toggleLeadStatusConfirmed}
                                disabled={isDeactivating}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                            >
                                {isDeactivating ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                        Deactivating...
                                    </>
                                ) : (
                                    'Deactivate Lead'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StudentDetails;